import React, { useCallback, useEffect, useState } from "react";
import "./OurCinemasSlider.css";

// ─────────────────────────────────────────────────────────────────────────────
//  OurCinemasSlider
//  Pure-React image slider for the public About Us "Our Cinemas" tab list.
//
//  Behaves like the static HTML reference .our-cinemas-slider.owl-carousel:
//    - Left/right arrows (matches .owl-nav buttons).
//    - End-of-loop arrows fade to opacity 0 (matches .owl-prev.disabled).
//    - No autoplay (manual only).
//  …with two additions on top:
//    - Dot indicators overlay the bottom of the image so users can jump
//      directly to any slide (matches AboutIntroSlider on the same page).
//    - Images render at their natural aspect ratio (width:100%, height:auto)
//      — no forced 278px box, no object-fit:cover cropping. Admin uploads
//      are shown exactly as uploaded.
//
//  Visual + interaction parity with the HTML reference:
//
//      $('.our-cinemas-slider').owlCarousel({
//          loop: true,
//          margin: 0,
//          nav: true,        // ← left/right arrow nav
//          dots: false,      // ← dots disabled in reference
//          items: 1,
//          autoplay: false,
//      });
//
//  - Horizontal translateX slide (owl default).
//  - 250ms ease transition (owl smartSpeed default).
//  - Single-image case: bare image, no arrows, no dots.
// ─────────────────────────────────────────────────────────────────────────────

const normalizeImageUrl = (entry) => {
  if (!entry) return null;
  const url = typeof entry === "string" ? entry : entry?.url;
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("http") || url.startsWith("/")) return url;
  return `/${url}`;
};

const OurCinemasSlider = ({
  images = [],
  fallbackImage = null,
  alt = "Our cinema",
}) => {
  const slides = (Array.isArray(images) ? images : [])
    .map((entry) => {
      const url = normalizeImageUrl(entry);
      if (!url) return null;
      const slideAlt =
        typeof entry === "object" && entry?.alt ? entry.alt : alt;
      return { url, alt: slideAlt };
    })
    .filter(Boolean);

  // Legacy single-image fallback: shown only when the slider is empty.
  const fallbackUrl = normalizeImageUrl(fallbackImage);

  const [activeIndex, setActiveIndex] = useState(0);

  // Reset index if slides shrink below the current value
  useEffect(() => {
    if (activeIndex >= slides.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, slides.length]);

  const goTo = useCallback(
    (idx) => {
      if (slides.length === 0) return;
      const next = ((idx % slides.length) + slides.length) % slides.length;
      setActiveIndex(next);
    },
    [slides.length],
  );

  // Nothing to render — caller decides whether to hide the area.
  if (slides.length === 0 && !fallbackUrl) return null;

  // Single fallback image only — no slider UI needed.
  if (slides.length === 0) {
    return (
      <div className="our-cinemas-slider">
        <div className="our-cinemas-slider__track">
          <figure className="our-cinemas-slider__figure">
            <img src={fallbackUrl} alt={alt} loading="lazy" />
          </figure>
        </div>
      </div>
    );
  }

  const isMulti = slides.length > 1;
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === slides.length - 1;

  return (
    <div className="our-cinemas-slider">
      <div className="our-cinemas-slider__track">
        <div
          className="our-cinemas-slider__track-inner"
          style={{
            // Inner track stays at width:100% of the track (set in
            // CSS) so it's exactly one viewport wide. Each slide is
            // `min-width:100%` of the inner = one viewport wide, so
            // N slides stack side-by-side at N*inner wide and overflow
            // the track (overflow:hidden clips them). Translating the
            // inner by -(activeIndex * 100)% shifts it by exactly one
            // viewport per step, which lines slide k+1 up at x=0.
            // Slides share the tallest image's height via the flex
            // row's align-items:stretch, so each image renders at its
            // natural aspect ratio (width:100%; height:auto on the
            // <img>) without any forced crop. Same pattern as the
            // working AboutIntroSlider on the same page.
            transform: `translateX(-${activeIndex * 100}%)`,
          }}
        >
          {slides.map((slide, i) => (
            <div
              key={`${slide.url}-${i}`}
              className="our-cinemas-slider__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${slides.length}`}
              aria-hidden={i !== activeIndex}
            >
              <figure className="our-cinemas-slider__figure">
                <img src={slide.url} alt={slide.alt} loading="lazy" />
              </figure>
            </div>
          ))}
        </div>

        {isMulti && (
          <>
            <button
              type="button"
              className={`our-cinemas-slider__nav our-cinemas-slider__nav--prev${
                isFirst ? " is-disabled" : ""
              }`}
              onClick={() => goTo(activeIndex - 1)}
              disabled={isFirst}
              aria-label="Previous slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="#2E3192"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className={`our-cinemas-slider__nav our-cinemas-slider__nav--next${
                isLast ? " is-disabled" : ""
              }`}
              onClick={() => goTo(activeIndex + 1)}
              disabled={isLast}
              aria-label="Next slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="#2E3192"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        {isMulti && (
          <div className="our-cinemas-slider__dots" role="tablist">
            {slides.map((slide, i) => (
              <button
                key={`dot-${i}`}
                type="button"
                className={`our-cinemas-slider__dot${
                  i === activeIndex ? " is-active" : ""
                }`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={i === activeIndex}
                role="tab"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OurCinemasSlider;
